import React, {useEffect, useRef, useState} from 'react';
import LocationForm from "../../../../../../../../../components/Location/LocationForm";
import {
    ApiError,
    ConfigAddressApi,
    ConfigApi,
    OrderType,
    PlaceApi
} from "../../../../../../../../../services/Api/types";
import {useTranslation} from "react-i18next";
import {ReactComponent as LocationSVG} from "../../../../../../../../../../images/icons/location.svg";
import {useGeolocation} from "../../../../../../../../../hooks/useGeolocation";
import {api} from "../../../../../../../../../services/Api/api";
import {useSessionService} from "../../../../../../../../../services/Session/sessionService";
import {SessionState} from "../../../../../../../../../services/Session/types";
import {usePlaceContext} from "../../../../../../../../../services/Place/PlaceContext";
import {getProperPlaceLink} from "../../../../../../../utils/multistoreUtils";
import {useNavigate} from "react-router-dom";
import MultistoreWithHomePagePlacesModal from "./MultistoreWithHomePagePlacesModal";
import MultistoreWithHomePageAddressModal from "./MultistoreWithHomePageAddressModal";
import {LocationFormRef} from "../../../../../../../../../services/types";

interface Props {
    setAddress: React.Dispatch<React.SetStateAction<ConfigAddressApi>>;
    address: ConfigAddressApi;
    setForceStreet: React.Dispatch<React.SetStateAction<boolean>>;
    forceStreet: boolean;
    setErrors: React.Dispatch<React.SetStateAction<ApiError[]>>;
    errors: ApiError[];
    config: ConfigApi | undefined;
    session: SessionState;
    placeLinkParams?: string[];
}

const MultistoreWithHomePageDeliveryForm = ({
                                                setAddress,
                                                address,
                                                forceStreet,
                                                setForceStreet,
                                                config,
                                                errors,
                                                setErrors,
                                                session,
                                                placeLinkParams
                                            }: Props) => {
    const {t} = useTranslation();
    const [loadingLocation, setLoadingLocation] = useState(false);
    const {handleGeolocationError, getPosition} = useGeolocation();
    const [loading, setLoading] = useState(false);
    const sessionService = useSessionService();
    const [showForm, setShowForm] = useState(!session.full_address ? true : JSON.stringify(address) !== JSON.stringify(session.address));
    const {places, placesLoading} = usePlaceContext();
    const navigate = useNavigate();
    const [placesForModal, setPlacesForModal] = useState<PlaceApi[] | undefined>();
    const [showChangeAddressModal, setShowChangeAddressModal] = useState(false);
    const [addressForAddressModal, setAddressForAddressModal] = useState<ConfigAddressApi | undefined>();
    const [forceUpdateAddressFormValue, setForceUpdateAddressFormValue] = useState(false);
    const formRef = useRef<LocationFormRef>(null);
    const [shouldRepeatContinueDelivery, setShouldRepeatContinueDelivery] = useState(false);

    useEffect(() => {
        if (!showForm) {
            handleCheckAddress(true);
        }
    }, []);

    useEffect(() => {
        if (!placesLoading && shouldRepeatContinueDelivery) {
            setShouldRepeatContinueDelivery(false);
            handleDeliveryContinue();
        }
    }, [placesLoading, shouldRepeatContinueDelivery]);

    const geolocation = async () => {
        try {
            setLoadingLocation(true);
            const position = await getPosition();
            const {latitude, longitude} = position.coords;
            const res = await api.getAddressByCoordinates({latitude, longitude});

            const resAddress = {
                street: res.street ?? "",
                city: res.city ?? "",
                country: res.country ?? "",
                zip_code: res.zip_code ?? "",
                flat_nr: res.flat_nr ?? "",
                build_nr: res.build_nr ?? "",
            };

            if (showForm) {
                setAddress(resAddress);
                setForceUpdateAddressFormValue(true);
            } else {
                setAddressForAddressModal(resAddress);
                setShowChangeAddressModal(true);
            }
        } catch (err) {
            handleGeolocationError(err as GeolocationPositionError);
        } finally {
            setLoadingLocation(false);
        }
    }

    const handleCheckAddress = async (preventDeliveryContinue = false) => {
        const formErrors = formRef.current?.validateAddress();

        if (formErrors && formErrors.length > 0) return;

        try {
            setLoading(true);
            const locationCheckRes = await api.getLocationCheck(address);
            await sessionService.updateLocation(address);

            if (!locationCheckRes.coordinates) {
                setErrors([{
                    "field": "location",
                    "code": "coordinates_not_exist",
                    message: ""
                }]);
            } else if (!locationCheckRes.in_delivery_area) {
                setErrors([{
                    "field": "location",
                    "code": "not_in_delivery_area",
                    message: ""
                }]);
            } else {
                setErrors([]);

                if (!preventDeliveryContinue) setShouldRepeatContinueDelivery(true);
            }

            setShowForm(false);
        } catch (error) {
            setErrors([{
                "field": "address",
                "code": "connection_problem",
                message: ""
            }]);
        } finally {
            setLoading(false);
        }
    }
    const handleDeliveryContinue = () => {
        if (placesLoading) return;

        const placesWithDelivery = (places ?? []).filter(place => place.types.includes(OrderType.DELIVERY) && place.in_delivery_zone);

        if (!placesWithDelivery.length) {
            setErrors([{
                "field": "location",
                "code": "not_in_delivery_area",
                message: ""
            }]);
        } else if (placesWithDelivery.length === 1) {
            let link = `${getProperPlaceLink(placesWithDelivery[0])}/DELIVERY`;

            if (placeLinkParams && placeLinkParams.length > 0) {
                link += `?${placeLinkParams.join("&")}`;
            }

            navigate(link);
        } else {
            setPlacesForModal(placesWithDelivery);
        }
    }

    const onHideAddressModal = () => {
        setAddressForAddressModal(undefined);
        setShowChangeAddressModal(false);
        setErrors((prevState) => prevState.filter(error => !error.field?.includes("address") && error.code !== "required"));
    }

    const isFormNextButtonDisabled = () => {
        if (forceStreet) {
            return !address.street && !address?.city && !address.build_nr;
        }

        if (address.street && !address.city) {
            return false;
        }

        return !address.city && !address.build_nr
    }

    return (
        <div>
            {
                showForm ? (
                    <>
                        <LocationForm
                            address={address}
                            forceStreet={forceStreet}
                            setForceStreet={setForceStreet}
                            config={config}
                            onErrors={setErrors}
                            onChange={setAddress}
                            errors={errors}
                            forceUpdateAddressFormValue={forceUpdateAddressFormValue}
                            setForceUpdateAddressFormValue={setForceStreet}
                            preventCheckingAddress
                            validateAddressBeforeSubmit
                            clearRequiredInputsErrorsOnChange
                            ref={formRef}
                        />
                        <button className={`btn btn-primary btn-lg w-100 btn-spinner ${loading ? "loading" : ""}`}
                                onClick={() => handleCheckAddress()}
                                disabled={isFormNextButtonDisabled()}
                        >
                            <span className="left spinner-border spinner-border-sm"/>
                            {t("modules.multistore.action.next.title")}
                        </button>
                    </>
                ) : (
                    <div className="d-flex gap-2">
                        <button className="btn btn-default w-100 btn-lg"
                                onClick={() => setShowChangeAddressModal(true)}>
                            {t("modules.multistore.action.change_address.title")}
                        </button>
                        <button
                            className={`btn btn-primary btn-spinner w-100 btn-lg`}
                            onClick={() => handleDeliveryContinue()}
                            disabled={errors.filter(error => error.field === "location").length > 0}
                        >
                            {t("modules.multistore.action.next.title")}
                        </button>
                    </div>
                )
            }
            <div className="horizontal-divider">
                <span className="text-muted">{t("common.word.or")}</span>
            </div>
            <button
                className={`btn btn-link btn-lg text-decoration-none w-100 btn-flex btn-spinner ${loadingLocation ? "loading" : ""}`}
                onClick={geolocation}>
                <LocationSVG/>
                <span className="left spinner-border spinner-border-sm"/>
                {t("modules.multistore.action.use_current_location.title")}
            </button>
            {
                placesForModal && (
                    <MultistoreWithHomePagePlacesModal
                        show={!!placesForModal}
                        places={placesForModal}
                        onHide={() => setPlacesForModal(undefined)}
                        session={session}
                        config={config}
                        placeLinkParams={placeLinkParams}
                    />
                )
            }
            {
                showChangeAddressModal && (
                    <MultistoreWithHomePageAddressModal show={showChangeAddressModal}
                                                        onHide={onHideAddressModal}
                                                        config={config}
                                                        session={session}
                                                        setErrors={setErrors}
                                                        errors={errors}
                                                        address={addressForAddressModal}
                    />
                )
            }
        </div>
    )
}

export default MultistoreWithHomePageDeliveryForm;